import {
  FORMAT_PAR_HABILLAGE,
  ecrireChecklist,
  formatDuNombre,
  PREFIXE_CHAMP_SOCLE,
  estCitationNative,
  formuleLisible,
  lireChecklist,
  optionsPourParent,
  recalculer,
  valeurLueSelonLeType,
  valeurNormalisee,
  viderLesDependants,
} from '@taki/data-model';
import type { Cell, Column, EditorKind, Option, ReglagesDeColonne, Tone } from './contract.js';
import { editeurEnListe, formatDeNombre, lignesDeCellule, lireBooleen, sansDoublon } from './champs-types.js';
import { rendreValeurDeChamp } from './rendus-de-champ.js';

/**
 * LES DERIVEURS DE CHAMPS : d'un `taki.fields.json` vers des colonnes.
 *
 * CE QUE CE MODULE EST. Une bibliotheque de fonctions PURES qui transforment de
 * la DONNEE (le fichier de champs d'une app) en objets du contrat des vues
 * (`Column`). Rien de plus.
 *
 * CE QU'IL N'EST PAS, ET C'EST LA LIGNE ROUGE DU CHANTIER. Ce n'est pas un
 * moteur qui INTERPRETE une specification d'ecran. Il ne lit aucune expression,
 * n'evalue aucun gabarit, ne construit aucune vue : il rend des colonnes qu'une
 * app compose, remplace ou enrichit comme elle veut. La difference tient en une
 * phrase : ce module ne sait faire que ce que le fichier DECLARE, et le fichier
 * ne declare que des faits (une clef, un libelle, un type, un catalogue). Le
 * jour ou une clef du fichier decrirait un CALCUL, ce module deviendrait un
 * langage maison, et un langage maison degrade exactement ce que les agents
 * savent le mieux faire : du React et du TypeScript ordinaires.
 *
 * COMMENT UNE APP S'EN ECARTE : elle passe ses propres colonnes. `colonnesDe`
 * rend un tableau ordinaire ; une app le filtre, le reordonne, remplace une
 * entree par la sienne, ou ajoute un `render` par-dessus. Aucune de ces trois
 * choses ne demande la permission du socle. Il n'y a pas de cas ou l'app doit
 * "contourner" ce module : il ne fait rien qu'on ne puisse refaire a la main.
 *
 * OU IL VIT, ET POURQUOI ICI. Dans `views/`, a cote du contrat qu'il produit :
 * son entree vient de `@taki/data-model` (le schema du fichier), sa sortie est
 * un `Column` de `./contract.js`. Le mettre dans une app le rendrait
 * inutilisable par les autres ; le mettre dans le data-model y ferait entrer le
 * vocabulaire d'affichage.
 */

/* ------------------------------------------------------------------ */
/* La forme consommee, decrite ici et importee de nulle part            */
/* ------------------------------------------------------------------ */

/**
 * LE TYPE D'UN CHAMP, tel que le contrat des vues en a besoin.
 *
 * POURQUOI IL EST REDECLARE ICI plutot qu'importe de `@taki/data-model` : ce
 * package est le DESIGN SYSTEM. Il ne connait aucune entite metier, aucune
 * famille, aucun schema Zod, et lui donner une dependance vers le modele de
 * donnees pour dix noms de types echangerait une frontiere nette contre une
 * commodite.
 *
 * CE N'EST PAS UN FAIT ECRIT DEUX FOIS, parce que le compilateur relie les
 * deux : une app qui passe ses `AppField[]` (issus du schema Zod) a
 * `colonnesDe` ne compile QUE si les deux formes concordent. Le jour ou le
 * schema du data-model gagne une valeur de `type`, ce fichier cesse de compiler
 * chez tous ses appelants. C'est du typage structurel, pas une copie.
 */
export type TypeDeChamp =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multiselect'
  | 'email'
  | 'phone'
  | 'url'
  | 'lien'
  /* UNE PERSONNE DE L'ORGANISATION : la valeur est son `personId`. Le socle
     lui donne une reference implicite vers l'annuaire des personnes
     (`views/screens.ts`), et elle suit ensuite tout le chemin d'un `lien`. */
  | 'personne'
  /* LES TYPES DU 27/10/2026 (`docs/socle/champs-types.md`), dans l'ordre du
     catalogue (`catalogue-des-types.mjs`). Aucun n'invente une forme de
     valeur : un nombre, une chaine ou une liste de chaines. Un prix, un
     pourcentage et une duree n'en sont pas : ce sont des `number` (`devise`,
     `affichage`, l'habillage de la colonne), et des liens multiples un `lien`
     qui porte `plusieurs`. */
  | 'etiquettes'
  | 'periode'
  | 'checklist'
  | 'note'
  | 'calcul'
  | 'numero';

/**
 * LA CIBLE D'UN CHAMP `lien` : la famille qu'il designe, telle que la
 * declaration la porte (`reference` d'un champ de `taki.fields.json`, ou d'une
 * colonne native de `taki.screens.json`).
 *
 * C'EST CE QUI REND UNE COLONNE CONNECTABLE SANS PORTE : avant, une colonne ne
 * designait une fiche que si une porte du meme ecran portait sa famille (le
 * sens inverse d'un lien, `liensEntrants`). Ici la colonne le DIT elle-meme, et
 * l'ecran n'a plus a monter la famille visee pour en afficher un nom.
 *
 * `titre` et `sousTitre` se lisent DANS LE VOCABULAIRE DE LA FAMILLE VISEE :
 * `socle:name` designe `name` a la racine de la fiche liee, `libelle` designe
 * `customFields.libelle` de cette meme fiche. Absents, le titre suit la
 * convention de la famille (`FonctionsDeclarees.titres`), puis l'id.
 */
export interface ReferenceDeclaree {
  family: string;
  type?: string;
  titre?: string;
  sousTitre?: string;
}

/** Une option de catalogue, telle que le fichier de champs la declare. */
export interface OptionDeclaree {
  archived?: boolean;
  value: string;
  label: string;
  /** Une clef de la grille du design ou un hex (`couleurs.ts`). */
  tone?: Tone;
  /** La valeur du champ parent a laquelle l'option appartient (`dependDe`). */
  parent?: string;
}

/** Un champ declare par une app, reduit a ce que les vues en font. */
export interface ChampDeclare {
  key: string;
  label: string;
  type: TypeDeChamp;
  required?: boolean;
  options?: readonly OptionDeclaree[];
  helpText?: string;
  /** La cible d'un champ `lien`, et rien pour les autres types. */
  reference?: ReferenceDeclaree;
  /**
   * L'EXEMPLE QU'UNE CASE VIDE MONTRE (« Sony », « PXW-FX9 », « 290 »).
   *
   * IL SE DECLARE DANS LE STUDIO, champ par champ, et il ne se devine pas : un
   * defaut fabrique depuis le libelle (« Marque... ») repeterait l'etiquette
   * juste au-dessus. Sans lui, la fiche de creation retombe sur le mot generique
   * de son type (`placeholderParDefaut`, `lib/creation.tsx`).
   */
  placeholder?: string;
  /** La clef du champ a choix dont les options de celui-ci dependent. */
  dependDe?: string;
  /*
   * LES REGLAGES DES TYPES DU 27/10/2026, tels que `taki.fields.json` les
   * porte (`custom-field.ts`, chacun reserve a ses types par le catalogue).
   * Ils ne changent que l'AFFICHAGE et la saisie : la valeur reste le nombre
   * nu, la chaine ou la liste (`ReglagesDeColonne`).
   */
  /** L'unite affichee apres un nombre (« m² »). `number`, `calcul`. */
  unite?: string;
  /** La devise qui fait d'un nombre un montant (code ISO 4217). `number`, `calcul`. */
  devise?: string;
  /** Les decimales montrees. `number`, `calcul`. */
  decimales?: number;
  /** `jauge` : un pourcentage de 0 a 100, montre et regle en jauge. `number`. */
  affichage?: 'jauge';
  /** Des liens multiples : la valeur est une liste d'ids. `lien`. */
  plusieurs?: boolean;
  /** La note maximale (5 par defaut). `note`. */
  max?: number;
  /** La formule d'un calcul, en forme STOCKEE (`{prix} * 1.2`). `calcul`. */
  formule?: string;
  /** La sequence d'un numero automatique. `numero` : attribue par le serveur, lu ici seulement. */
  sequence?: { modele: string; chiffres?: number };
}

/* ------------------------------------------------------------------ */
/* Traduire le vocabulaire                                             */
/* ------------------------------------------------------------------ */

/**
 * LES CHAMPS D'UN JEU, par sa famille et son type local.
 *
 * Rend un tableau VIDE quand le jeu n'est pas declare, jamais une exception :
 * une app peut monter une porte sur un jeu qu'elle ne personnalise pas, et
 * c'est un fait, pas une faute.
 */
export function champsDuJeu(
  fichier: { jeux: readonly { family: string; type?: string; champs: readonly ChampDeclare[] }[] },
  family: string,
  type?: string,
): readonly ChampDeclare[] {
  const jeu = fichier.jeux.find((j) => j.family === family && j.type === type);
  return jeu?.champs ?? [];
}

/**
 * LE TYPE DE DONNEE devient un TYPE D'EDITEUR.
 *
 * Deux vocabulaires, et ils ne se confondent pas : `CustomFieldType` dit ce que
 * la valeur EST (orientee donnee), `EditorKind` dit comment on la
 * SAISIT (ce que le socle sait rendre aujourd'hui). Une table
 * pure les relie, et c'est le seul endroit du depot ou la traduction existe.
 *
 * `boolean` réutilise le choix simple Oui/Non. La colonne convertit les
 * valeurs du sélecteur en booléens avant de les transmettre à l'accès.
 *
 * `multiselect` A LE SIEN depuis les plans recurrents (leurs jours de semaine) :
 * la meme liste que le catalogue, ou chaque clic COCHE ou DECOCHE au lieu de
 * choisir. Il n'est modifiable, comme tous, que si l'app fournit l'ecriture.
 *
 * UN NOMBRE A UN SEUL EDITEUR, QUEL QUE SOIT SON FORMAT : un prix, un
 * pourcentage, une jauge et une duree sont des `number`, et l'editeur `number`
 * lit le format dans les reglages de la colonne (`formatDuNombre`). Seuls des
 * liens MULTIPLES changent d'editeur (`liens`) : ils ajoutent et retirent.
 *
 * `lien` PART EN `text` ICI, ET C'EST L'ASSEMBLEUR QUI LE RELEVE : l'editeur
 * `lien` (un choix dans un annuaire) n'a de sens qu'avec la REFERENCE de la
 * colonne, que seul `catalogueDeColonnes` pose (`referencer`, `screens.ts`).
 * Une app TypeScript qui appelle `colonnesDe` a la main sans passer par lui
 * garde une case ou l'on tape un id : un editeur qui promet un annuaire qu'il
 * n'a pas serait le mensonge d'au-dessus.
 */
const EDITEUR_PAR_TYPE: Record<TypeDeChamp, EditorKind | undefined> = {
  text: 'text',
  textarea: 'multiline',
  number: 'number',
  boolean: 'boolean',
  date: 'date',
  datetime: 'datetime',
  select: 'select',
  multiselect: 'multiselect',
  email: 'text',
  phone: 'text',
  url: 'text',
  lien: 'text',
  /* Comme `lien` : l'assembleur le releve en `lien` des que la colonne porte sa
     reference et s'ecrit (`catalogueDeColonnes`), donc un choix par nom. */
  personne: 'text',
  etiquettes: 'etiquettes',
  periode: 'periode',
  checklist: 'checklist',
  note: 'note',
  calcul: undefined,
  numero: undefined,
};

/**
 * Le type d'editeur d'un champ, ou `undefined` si le socle ne sait pas le
 * saisir. Des liens multiples (`plusieurs`) ont le leur, `liens`, deja pose :
 * il n'attend aucune reference pour ajouter et retirer des ids.
 */
export const editeurDe = (champ: ChampDeclare): EditorKind | undefined =>
  champ.type === 'lien' && champ.plusieurs === true ? 'liens' : EDITEUR_PAR_TYPE[champ.type];

/** Le catalogue d'un champ de choix, au format du contrat des vues. */
export function libelleOption(option: { label: string; archived?: boolean }): string {
  return option.archived ? `${option.label} (archivée)` : option.label;
}

export function optionsDe(champ: ChampDeclare): Option[] {
  if (champ.type === 'boolean') return [
    { value: 'true', label: 'Oui', tone: 'neutral' },
    { value: 'false', label: 'Non', tone: 'neutral' },
  ];
  return (champ.options ?? []).map((o) => ({
    value: o.value,
    label: libelleOption(o),
    ...(o.archived ? { archived: true } : {}),
    tone: o.tone ?? 'neutral',
  }));
}

/* ------------------------------------------------------------------ */
/* Deriver des colonnes                                                */
/* ------------------------------------------------------------------ */

/**
 * LES CLEFS D'UN CHOIX MULTIPLE, lues dans une cellule.
 *
 * UNE SEULE LECTURE POUR TROIS USAGES, et c'est pour ca qu'elle est exportee :
 * le RENDU (« Lundi, Vendredi »), l'EDITEUR (quelles cases sont cochees) et
 * l'ECRITURE (la liste qui part au SDK) doivent decouper la meme valeur de la
 * meme facon. Trois decoupages ecrits a la main divergeraient au premier
 * separateur oublie, et la coche mentirait sur ce qui est stocke.
 *
 * LA VALEUR ARRIVE UNE CLEF PAR LIGNE (la lecture du socle rend une liste
 * ainsi, `cellule` dans `app-runtime/src/acces.ts`), ou collee par des virgules
 * chez une app qui la lit elle-meme. Vide, `null` ou blanc : aucune clef.
 */
export function clefsDuChoixMultiple(valeur: Cell | undefined): string[] {
  if (valeur === null || valeur === undefined) return [];
  return String(valeur)
    .split(/[\n,]/)
    .map((clef) => clef.trim())
    .filter((clef) => clef !== '');
}

/**
 * CE QU'UNE ECRITURE DE CHAMP PEUT PORTER : une cellule, ou une LISTE de clefs.
 *
 * LA LISTE N'EXISTE QU'ICI, a la frontiere avec l'app, et jamais dans les vues :
 * un choix multiple se stocke en tableau (le schema de la famille l'exige pour
 * un natif, et un sac ne doit pas relire « lun\nven » comme une seule clef),
 * mais il circule dans le moteur sous sa forme de cellule. Seule la colonne
 * `multiselect` en emet une (`colonnesDe`), toujours un tableau NEUF, vide
 * compris : decocher tout ecrit `[]`, pas `null`.
 */
export type ValeurEcrite = Cell | readonly string[];

/** Ce que l'app doit fournir : lire et ecrire le sac de son enregistrement. */
export interface AccesChamps<T> {
  /** La valeur brute d'une clef du sac, telle qu'elle est stockee. */
  lire(record: T, key: string): Cell;
  /**
   * La valeur d'un champ NATIF de la famille, a la racine de la fiche. Elle ne
   * sert qu'aux calculs qui citent un natif (`{socle:basePrice}`) ; absente, un
   * natif cite se lit comme vide.
   */
  natif?(record: T, nom: string): Cell;
  /**
   * L'ecriture d'une clef. ABSENTE = les colonnes derivees ne portent pas de
   * `set`, donc rien n'est modifiable : c'est le regime d'une app en lecture.
   */
  ecrire?(record: T, key: string, valeur: ValeurEcrite): Promise<void>;
}

/**
 * Ce qu'on peut regler par colonne, sans reecrire la colonne entiere.
 *
 * TYPE PAR L'ENREGISTREMENT, et pas par `unknown` : un helper de socle qui
 * oblige son appelant a caster n'a pas fait son travail, il l'a deplace.
 */
export interface ReglagesColonne<T> {
  /** Colonne triable au clic. Defaut : vrai, sauf pour un texte long. */
  sortable?: boolean;
  /** Agregat de pied de colonne. */
  agg?: 'sum' | 'count' | 'avg';
  /** Habillage seul. Le tri, l'export et l'agregat gardent la valeur brute. */
  render?: Column<T>['render'];
  /**
   * L'HABILLAGE QUE LA DECLARATION D'ECRAN DONNE A LA COLONNE (`euros`,
   * `pourcentage`, `duree`), par son NOM : c'est ainsi que l'existant code un
   * prix, un avancement ou un temps alloue. Il donne son FORMAT au nombre
   * (`formatDuNombre`), donc son editeur, son pied et son filtre, comme une
   * `devise` ou une jauge declarees.
   */
  habillage?: string;
}

/**
 * UNE COLONNE PAR CHAMP DECLARE, dans l'ordre du fichier.
 *
 * CE QU'ELLE PORTE : la clef, le libelle, la lecture, le type d'editeur, le
 * catalogue, et l'ecriture quand l'app en fournit une. C'est exactement ce
 * qu'une app ecrivait a la main, colonne par colonne, avec le meme corps a
 * chaque fois.
 *
 * CE QU'ELLE NE PORTE PAS : aucun `render`. Le formatage est une decision
 * d'app (des euros, une duree, une initiale), et le fichier ne le declare pas.
 * Une app qui veut habiller une colonne la reprend apres coup :
 *
 *     const colonnes = colonnesDe(champs, acces).map((c) =>
 *       c.key === 'dailyRate' ? { ...c, render: (a) => formatEuros(...) } : c,
 *     );
 *
 * `sortable` vaut vrai par defaut, sauf sur un texte long : trier sur un
 * paragraphe n'a jamais aide personne, et l'en-tete cliquable promettrait un
 * classement qui ne veut rien dire.
 */
export function colonnesDe<T>(
  champs: readonly ChampDeclare[],
  acces: AccesChamps<T>,
  reglages: Readonly<Record<string, ReglagesColonne<T>>> = {},
  /**
   * LE JEU ENTIER, pour rendre lisible la formule d'un `calcul` (elle cite les
   * AUTRES champs par leur clef, `formuleLisible`). Par defaut les champs
   * passes : l'assembleur, qui batit une colonne a la fois, passe le jeu.
   */
  jeu: readonly ChampDeclare[] = champs,
  /** Le fuseau de l'org (nom IANA) : celui dans lequel un `datetime` se lit. */
  fuseau?: string,
): Column<T>[] {
  return champs.map((champ) => {
    const regle = reglages[champ.key] ?? {};
    /* UN CHOIX MULTIPLE SANS CATALOGUE NE S'EDITE PAS : sa liste s'ouvrirait
       vide, une case qui invite au clic pour ne rien proposer. Il reste
       lisible, en lecture seule. */
    const sansCatalogue = champ.type === 'multiselect' && (champ.options ?? []).length === 0;
    const editeur = sansCatalogue ? undefined : editeurDe(champ);
    const ecrire = acces.ecrire;
    const base = reglagesDe(champ, jeu, regle.habillage);
    const affichage = champ.type === 'datetime' && fuseau !== undefined ? { ...base, fuseau } : base;

    const colonne: Column<T> = {
      key: champ.key,
      label: champ.label,
      value: (record) => valeurLueSelonLeType(champ.type, acces.lire(record, champ.key)),
      sortable: regle.sortable ?? champ.type !== 'textarea',
      /* LE TYPE DECLARE VOYAGE AVEC LA COLONNE, en lecture comme en ecriture :
         c'est lui, avec les reglages qui portent le format, que lisent les
         filtres, le pied d'un montant et la projection d'une liste de liens,
         sur une colonne qui n'a pas forcement d'editeur (un calcul, un numero). */
      valueType: champ.type,
    };
    if (affichage !== undefined) colonne.reglages = affichage;
    if (regle.agg !== undefined) colonne.agg = regle.agg;
    /*
     * LE RENDU PAR TYPE, et seulement l'AFFICHAGE : « 240 m² », « 1 250 € »,
     * des pastilles pour un choix, des etoiles pour une note
     * (`rendreValeurDeChamp`, `core/ChampsRiches.tsx`). La valeur brute reste
     * celle du tri, de l'export et de l'agregat. Ce n'est pas un formatage
     * decide par l'app : c'est le SENS du type, et le fichier le declare deja.
     * Un type sans rendu propre (un texte, une date, un lien que l'annuaire
     * nomme) n'en recoit pas : il garde exactement son affichage d'avant.
     *
     * UN CHOIX SE LIT PAR SES LIBELLES (« Lundi », « Vendredi »), pas par ses
     * clefs : sans ce rendu, une colonne a choix montrait la clef brute
     * (« bonEtat »), et une app nee du Studio n'a aucun TypeScript ou ecrire
     * l'habillage. Un habillage nomme par la declaration le remplace, et c'est
     * ce que `catalogueDeColonnes` fait apres coup.
     */
    const catalogue = champ.type === 'select' || champ.type === 'multiselect' ? optionsDe(champ) : undefined;
    const rendu = rendreValeurDeChamp(champ.type, affichage, catalogue);
    if (rendu !== undefined) colonne.render = (record) => rendu(valeurLueSelonLeType(champ.type, acces.lire(record, champ.key)));
    /* ET SON TOTAL, quand le type a un format : un pied de colonne en m² ou en
       euros s'ecrit comme ses cases. Un habillage declare le remplace. */
    const format = formatDeNombre(champ.type, affichage);
    if (format !== undefined) colonne.formatAgg = (valeur) => format(valeur);
    if (regle.render !== undefined) colonne.render = regle.render;
    /*
     * LE PLACEHOLDER ET L'OBLIGATION VOYAGENT AVEC LA COLONNE, en DONNEE : ce
     * sont deux faits du champ, et la fiche de creation est le seul endroit qui
     * les rende. Les redemander au fichier de champs depuis la fiche
     * l'obligerait a connaitre `taki.fields.json`, qu'elle ne voit pas : elle ne
     * recoit que des colonnes.
     */
    if (champ.placeholder !== undefined) colonne.placeholder = champ.placeholder;
    if (champ.required === true) colonne.required = true;
    /* UN CALCUL SE RECALCULE SUR LA FICHE LUE, par l'evaluateur du data-model
       (`recalculer`, sur le jeu entier : un calcul qui en cite un autre passe
       apres lui). Une formule fautive n'est pas recalculee : la valeur lue reste. */
    if (champ.type === 'calcul') {
      colonne.calcule = (record) => {
        /* UN NATIF CITE (`socle:basePrice`) SE LIT A LA RACINE de la fiche, pas dans le sac. */
        const sac = Object.fromEntries(jeu.map((autre) => [
          autre.key,
          estCitationNative(autre.key) ? acces.natif?.(record, autre.key.slice(PREFIXE_CHAMP_SOCLE.length)) ?? null : acces.lire(record, autre.key),
        ]));
        const calculs = recalculer(jeu, sac);
        return Object.hasOwn(calculs, champ.key) ? (calculs[champ.key] ?? null) : valeurLueSelonLeType(champ.type, acces.lire(record, champ.key));
      };
    }
    if (editeur !== undefined) {
      colonne.kind = editeur;
      /*
       * LE CATALOGUE EST UNE FONCTION, pas un tableau, parce que le contrat le
       * veut ainsi : il se resout au RUNTIME. Aujourd'hui il rend le defaut
       * declare par l'app ; le jour ou un client redefinit ses valeurs, c'est
       * CE point precis qui ira les chercher, et aucune app n'aura a changer.
       * Le choix multiple coche parmi le meme catalogue.
       */
      if ((editeur === 'select' || editeur === 'multiselect') && catalogue !== undefined) {
        colonne.options = () => catalogue;
      }
      if (ecrire !== undefined) colonne.set = ecritureDe(champ, editeur, affichage, ecrire, regle.habillage);
    }
    return colonne;
  });
}

/**
 * LES REGLAGES D'AFFICHAGE D'UN CHAMP, en donnee (`ReglagesDeColonne`), ou
 * rien quand il n'en declare aucun. La formule d'un calcul y part deja
 * LISIBLE (« Prime annuelle * 1,2 ») : la vue ne connait pas le jeu, elle ne
 * saurait pas retrouver un libelle depuis une clef.
 *
 * L'HABILLAGE DE LA COLONNE Y ENTRE quand il donne un format a un nombre
 * (`FORMAT_PAR_HABILLAGE` : `euros`, `pourcentage`, `duree`) : l'editeur, le
 * pied, le filtre et le PDF le lisent la, comme une devise. Un autre habillage
 * (une date, une fonction de l'app) n'est que du rendu, et n'y entre pas.
 */
export function reglagesDe(champ: ChampDeclare, jeu: readonly ChampDeclare[], habillage?: string): ReglagesDeColonne | undefined {
  const reglages: ReglagesDeColonne = {
    ...(champ.unite === undefined ? {} : { unite: champ.unite }),
    ...(champ.devise === undefined ? {} : { devise: champ.devise }),
    ...(champ.decimales === undefined ? {} : { decimales: champ.decimales }),
    ...(champ.affichage === undefined ? {} : { affichage: champ.affichage }),
    ...(champ.plusieurs === true ? { plusieurs: true } : {}),
    ...(champ.max === undefined ? {} : { max: champ.max }),
    ...(champ.formule === undefined ? {} : { formule: formuleLisible(champ.formule, jeu) }),
    ...(habillage !== undefined && Object.hasOwn(FORMAT_PAR_HABILLAGE, habillage) ? { habillage } : {}),
  };
  return Object.keys(reglages).length === 0 ? undefined : reglages;
}

/**
 * L'ECRITURE D'UNE COLONNE DERIVEE : la cellule de l'editeur, ramenee a la
 * forme que le champ STOCKE. C'est le seul endroit qui sache encore le type du
 * champ au moment d'ecrire, donc le seul ou la traduction peut vivre.
 *
 * - UNE LISTE REDEVIENT UN TABLEAU ICI, ET NULLE PART AILLEURS : l'editeur
 *   emet les elements un par ligne (ou `null` quand tout est retire), l'app
 *   recoit un TABLEAU, vide compris, qu'elle ecrit tel quel. Un choix multiple
 *   relit ses clefs comme avant (`clefsDuChoixMultiple`) ; une liste de
 *   controle repasse par sa forme `[x] texte` (`ecrireChecklist`).
 * - UN OUI / NON S'ECRIT EN BOOLEEN, meme quand l'editeur ou la donnee d'avant
 *   le portent en chaine (`"true"`).
 * - UN NOMBRE A FORMAT (montant, pourcentage, jauge, duree : `formatDuNombre`,
 *   habillage de la colonne compris), une note et une periode passent par la
 *   porte d'entree du data-model (`valeurNormalisee`) : une saisie hors de ses
 *   bornes est REFUSEE avec la forme attendue, jamais tronquee en silence. Un
 *   nombre sans format s'ecrit tel que l'editeur l'a lu, comme avant.
 */
function ecritureDe<T>(
  champ: ChampDeclare,
  editeur: EditorKind,
  affichage: ReglagesDeColonne | undefined,
  ecrire: NonNullable<AccesChamps<T>['ecrire']>,
  habillage?: string,
): NonNullable<Column<T>['set']> {
  if (champ.type === 'boolean') {
    return async (record, valeur) => {
      const lu = typeof valeur === 'number' ? null : lireBooleen(valeur);
      if (lu === null) throw new Error('Choisis Oui ou Non.');
      await ecrire(record, champ.key, lu);
    };
  }
  if (editeurEnListe(editeur)) {
    return (record, valeur) => ecrire(record, champ.key, elementsDeListe(champ.type, valeur));
  }
  const nombreAFormat = champ.type === 'number' && formatDuNombre(champ, habillage) !== null;
  if (nombreAFormat || champ.type === 'note' || champ.type === 'periode') {
    return async (record, valeur) => {
      if (valeur === null || valeur === '') {
        await ecrire(record, champ.key, null);
        return;
      }
      const lu = valeurNormalisee(
        {
          key: champ.key,
          label: champ.label,
          type: champ.type,
          ...(affichage?.max === undefined ? {} : { max: affichage.max }),
          ...(champ.devise === undefined ? {} : { devise: champ.devise }),
          ...(champ.affichage === undefined ? {} : { affichage: champ.affichage }),
        },
        valeur instanceof Date ? valeur.toISOString() : valeur,
        undefined,
        habillage,
      );
      if ('refus' in lu) throw new Error(`« ${champ.label} » attend ${lu.refus}.`);
      await ecrire(record, champ.key, lu.valeur as Cell);
    };
  }
  return (record, valeur) => ecrire(record, champ.key, valeur);
}

/**
 * LES ELEMENTS D'UNE LISTE, PAR TYPE, tels qu'ils s'ECRIVENT : les clefs d'un
 * choix multiple (virgule comprise, comme avant), les etiquettes et les ids
 * sans doublon, une ligne par element, et une liste de controle dans sa forme
 * stockee. Toujours un tableau NEUF, vide compris : tout retirer ecrit `[]`.
 */
export function elementsDeListe(type: TypeDeChamp | EditorKind | undefined, valeur: Cell | undefined): string[] {
  if (type === 'multiselect') return clefsDuChoixMultiple(valeur);
  if (type === 'checklist') return ecrireChecklist(lireChecklist(lignesDeCellule(valeur)));
  return sansDoublon(lignesDeCellule(valeur));
}

/**
 * LES OPTIONS DE TRI d'un ecran, derivees des champs triables.
 *
 * L'app y ajoute les siennes (un champ natif, une date du socle) : ce module
 * n'a rien a dire des champs qu'il ne connait pas.
 */
export function trisDe(champs: readonly ChampDeclare[]): { key: string; label: string }[] {
  return champs
    .filter((champ) => champ.type !== 'textarea')
    .map((champ) => ({ key: champ.key, label: champ.label }));
}

/**
 * LES CHAMPS OBLIGATOIRES d'un jeu, pour un formulaire de creation.
 *
 * Le socle ne fabrique pas le formulaire : il dit ce qui manque. Ce qu'on fait
 * d'un champ vide (bloquer, avertir, remplir par defaut) est une decision d'app.
 */
export const requisDe = (champs: readonly ChampDeclare[]): readonly ChampDeclare[] =>
  champs.filter((champ) => champ.required === true);

/**
 * LES CHOIX DEPENDANTS, BRANCHES SUR LES COLONNES DEJA BATIES (`dependDe`,
 * `@taki/data-model`). La colonne d'un champ dependant gagne son catalogue par
 * fiche (`optionsPour`) ; celle de son parent dit ce qu'elle vide en changeant
 * (`videAussi`). Une colonne absente ne recoit rien : l'ecran ne la montre pas.
 */
export function brancherLesDependances<T>(
  colonnes: ReadonlyMap<string, Column<T>>,
  champs: readonly ChampDeclare[],
  acces: AccesChamps<T>,
): void {
  const lire = (record: T, key: string): unknown => {
    const valeur = acces.lire(record, key);
    return champs.find((champ) => champ.key === key)?.type === 'multiselect' ? clefsDuChoixMultiple(valeur) : valeur;
  };
  for (const champ of champs) {
    if (champ.dependDe === undefined) continue;
    const parent = champ.dependDe;
    const colonne = colonnes.get(champ.key);
    if (colonne !== undefined && colonne.options !== undefined) {
      colonne.optionsPour = (record) => optionsDe({ ...champ, options: optionsPourParent(champ, lire(record, parent)) });
    }
    const colonneParent = colonnes.get(parent);
    if (colonneParent !== undefined && colonneParent.videAussi === undefined) {
      colonneParent.videAussi = (record, valeur) => viderLesDependants(champs, parent, valeur, (key) => lire(record, key));
    }
  }
}